import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Tenant } from '../types/dashboard';
import { apiService, ApiError, COOKIE_SESSION } from '../services/apiService';

/** Extract user-facing message from auth API errors (FastAPI detail or ApiError.response). */
function getAuthErrorMessage(err: unknown, fallback: string): string {
  const response = err instanceof ApiError ? err.response : (err as any)?.response;
  if (response && typeof response === 'object') {
    const d = response.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length > 0) {
      const parts = d.map((item: unknown) => {
        if (typeof item === 'object' && item !== null) {
          const raw = (item as any).msg ?? (item as any).message;
          if (typeof raw === 'string') {
            return raw.replace(/^Value error,\s*/i, '').trim();
          }
        }
        return typeof item === 'string' ? item : '';
      }).filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    if (response.message && typeof response.message === 'string') return response.message;
  }
  return fallback;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  isAuthenticated: boolean;
  /** True only while restoring session from storage (blocks app shell). */
  isInitializing: boolean;
  /** True during login/register API calls (disables form; does not unmount login UI). */
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (userData: { email: string; password: string; firstName: string; lastName: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  /** Merge updated fields into the current user (and persist to storage). */
  updateUser: (partial: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user && !!token;

  // Initialize auth state from localStorage. Use a minimum delay so we never flash
  // login before restore (avoids flash of login during session restore).
  useEffect(() => {
    const minLoadingMs = 200;
    const started = Date.now();
    const initializeAuth = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('user');
        const savedTenant = localStorage.getItem('tenant');

        if (savedUser && savedTenant && savedToken !== 'mock-jwt-token' && !savedToken?.startsWith('mock-jwt-token')) {
          const tenantObj = JSON.parse(savedTenant);
          apiService.setAuthToken(null);
          setToken(COOKIE_SESSION);
          setUser(JSON.parse(savedUser));
          setTenant(tenantObj);

          try {
            const legacyRefresh = localStorage.getItem('refreshToken');
            if (legacyRefresh && legacyRefresh.split('.').length === 3) {
              // Exchange the old localStorage refresh JWT for an HttpOnly cookie.
              await apiService.refreshSession();
            } else if (!savedToken) {
              await apiService.getCurrentUser(COOKIE_SESSION);
            } else {
              throw new ApiError('Session expired', 401);
            }
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
          } catch (e) {
            if (e instanceof ApiError && e.status === 401) {
              try {
                const refreshed = await apiService.refreshSession();
                if (!refreshed.data?.access_token) {
                  throw e;
                }
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
              } catch {
                localStorage.removeItem('user');
                localStorage.removeItem('tenant');
                apiService.setAuthToken(null);
                apiService.setRefreshToken(null);
                setToken(null);
                setUser(null);
                setTenant(null);
                setError('Your session expired. Please sign in again.');
              }
            }
          }
        } else {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          apiService.setAuthToken(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        const elapsed = Date.now() - started;
        const remaining = Math.max(0, minLoadingMs - elapsed);
        setTimeout(() => setIsInitializing(false), remaining);
      }
    };

    initializeAuth();
  }, []);

  // Keep apiService in sync with token so help and all API calls always have current auth
  useEffect(() => {
    apiService.setAuthToken(token);
  }, [token]);

  useEffect(() => {
    const onRefreshed = (ev: Event) => {
      const access = (ev as CustomEvent<{ accessToken?: string }>).detail?.accessToken;
      if (access) {
        setToken(access);
        setError(null);
      }
    };
    window.addEventListener('branchrouter:token-refreshed', onRefreshed);
    return () => {
      window.removeEventListener('branchrouter:token-refreshed', onRefreshed);
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      console.log('Attempting login via API for email:', email);
      
      // Call real API for login
      try {
        const response = await apiService.login(email, password);

        if (!response || !response.success) {
          console.error('Login failed - response not successful:', response);
          const errorMessage = response?.message || 'Invalid email or password';
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }
        
        if (!response.data) {
          console.error('Login failed - no data in response:', response);
          setError('Login failed. Please try again.');
          return { success: false, error: 'No data in response' };
        }
        
        // Backend returns { access_token, refresh_token, token_type, expires_in, user }
        const token = response.data.access_token;
        const refreshTok = response.data.refresh_token;
        const user = response.data.user;
        
        if (!token) {
          console.error('Login response missing token:', response.data);
          setError('Login response is invalid. Please try again.');
          return { success: false, error: 'Missing token in response' };
        }
        
        if (!user) {
          console.error('Login response missing user:', response.data);
          setError('Login response is invalid. Please try again.');
          return { success: false, error: 'Missing user in response' };
        }
        
        // Create user object
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim() || user.email || 'User';
        
        const userId = user.id || user.user_id;
        if (!userId) {
          console.error('User object missing id:', user);
          setError('Login response is invalid. Please try again.');
          return { success: false, error: 'Missing user ID' };
        }
        
        const userObj: User = {
          id: parseInt(String(userId), 10),
          tenantId: user.tenant_id || 'default',
          email: user.email,
          name: fullName,
          role: user.role || (user.is_admin ? 'admin' : 'user'),
          createdAt: user.created_at || new Date().toISOString(),
          preferences: {
            theme: 'light',
            notifications: true,
            emailAlerts: true,
            lowConfidenceAlerts: true
          }
        };

        // Create tenant object
        const tenantObj: Tenant = {
          id: user.tenant_id || 'default',
          name: user.tenant_name || 'Default Tenant',
          domain: user.tenant_domain || 'example.com',
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        setUser(userObj);
        setTenant(tenantObj);
        setToken(COOKIE_SESSION);

        // Session is an HttpOnly cookie. Do not keep the JWT in localStorage.
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.setItem('user', JSON.stringify(userObj));
        localStorage.setItem('tenant', JSON.stringify(tenantObj));
        apiService.setAuthToken(null);
        apiService.setRefreshToken(null);
        void token;
        void refreshTok;

        setError(null);
        return { success: true };
      } catch (apiError: any) {
        console.error('API login failed:', apiError);
        const errorMessage = getAuthErrorMessage(apiError, 'Invalid email or password. If you just registered, try signing in.');
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      return { success: false, error: 'Login failed. Please try again.' };
    } finally {
      setIsSubmitting(false);
    }
  };

  const register = async (userData: { email: string; password: string; firstName: string; lastName: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Try real API registration first
      try {
        const response = await apiService.registerUser(userData);

        if (!response || !response.success) {
          console.error('Registration failed - response not successful:', response);
          setError('Registration failed. Please try again.');
          return { success: false, error: 'Registration failed' };
        }
        
        if (!response.data) {
          console.error('Registration failed - no data in response:', response);
          setError('Registration failed. Please try again.');
          return { success: false, error: 'No data in response' };
        }
        
        const token = response.data.token;
        const user = response.data.user;
        
        if (!token) {
          console.error('Registration response missing token:', response.data);
          setError('Registration response is invalid. Please try again.');
          return { success: false, error: 'Missing token in response' };
        }
        
        if (!user) {
          console.error('Registration response missing user:', response.data);
          setError('Registration response is invalid. Please try again.');
          return { success: false, error: 'Missing user in response' };
        }
        
        // Create user object
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim() || user.email || 'User';
        
        const userId = user.id || user.user_id;
        if (!userId) {
          console.error('User object missing id:', user);
          setError('Registration response is invalid. Please try again.');
          return { success: false, error: 'Missing user ID' };
        }
        
        const userObj: User = {
          id: parseInt(String(userId), 10),
          tenantId: user.tenant_id || 'default',
          email: user.email,
          name: fullName,
          role: user.role || 'user',
          createdAt: user.created_at || new Date().toISOString(),
          preferences: {
            theme: 'light',
            notifications: true,
            emailAlerts: true,
            lowConfidenceAlerts: true
          }
        };

        // Create tenant object
        const tenantObj: Tenant = {
          id: user.tenant_id || 'default',
          name: user.tenant_name || 'Default Tenant',
          domain: user.tenant_domain || 'example.com',
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        setUser(userObj);
        setTenant(tenantObj);
        setToken(COOKIE_SESSION);

        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.setItem('user', JSON.stringify(userObj));
        localStorage.setItem('tenant', JSON.stringify(tenantObj));
        apiService.setAuthToken(null);
        void token;

        setError(null);
        return { success: true };
      } catch (apiError: any) {
        console.error('API registration failed:', apiError);
        const errorMessage = getAuthErrorMessage(apiError, 'Registration failed. Please try again or contact support.');
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
      return { success: false, error: 'Registration failed. Please try again.' };
    } finally {
      setIsSubmitting(false);
    }
  };

  const logout = () => {
    const finish = () => {
      apiService.setAuthToken(null);
      apiService.setRefreshToken(null);
      setUser(null);
      setTenant(null);
      setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      window.location.href = '/login';
    };
    // Clear the HttpOnly cookies before leaving. Navigation waits so the
    // request is not cancelled.
    void apiService.logout().finally(finish);
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshed = await apiService.refreshSession();
      const access = refreshed.data?.access_token;
      if (access) {
        setToken(access);
        setError(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      setError('Your session expired. Please sign in again.');
      logout();
      return false;
    }
  };

  useEffect(() => {
    apiService.setSessionExpiredHandler(() => {
      setError('Your session expired. Please sign in again.');
      logout();
    });
    return () => apiService.setSessionExpiredHandler(null);
  });

  const updateUser = (partial: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem('user', JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  };

  const value: AuthContextType = {
    user,
    tenant,
    token,
    isAuthenticated,
    isInitializing,
    isLoading: isSubmitting,
    error,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
